import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
    Box,
    Container,
    FormControl,
    Grid2,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    Input,
    CircularProgress,
    ButtonGroup,
    Button,
    Checkbox,
    ListItemText,
    Alert,
} from "@mui/material";
import { ChannelInsightsDisplay } from "./components";
import API_ENDPOINTS from "../../../apis/endpoints"; // Assuming this is correctly imported
import useSearchParams from "../../../hooks/use-search-params";
import useAuthAccessApi from "../../../hooks/use-auth-access-api";
import useSWR from "swr";
import { DownloadIcon } from "lucide-react";

const dateInputStyle = {
    width: 220,
    border: "1px solid #ccc",
    borderRadius: 1,
    px: 1,
    py: 0.5,
    "&:hover": { borderColor: "primary.main" },
    "&:before, &:after": { display: "none" },
};

const SupportedPlatforms = [
    { name: "instagram", label: "Instagram" },
    { name: "youtube", label: "YouTube" },
];

const ChannelAccountList = [
    {
        id: "sanatani",
        name: "Sanatani",
        platforms: {
            instagram: {
                id: "24911409738553119",
                username: "sanataniitihaas",
            },
            youtube: {
                id: "UCj68YHxp_glMUjDc9oHwQUA",
                username: "@SanataniCMN",
            },
        },
    },
    {
        id: "sanatani-music",
        name: "Sanatani Music",
        platforms: {
            youtube: {
                id: "UC8VtUwZInOs0kLafnFHr__A",
                username: "@SanatanimusicCMN",
            },
        },
    },
    {
        id: "chiranjeevi",
        name: "Chiranjeevi",
        platforms: {
            instagram: {
                id: "26559422030340958",
                username: "chiranjeevihanumanofficial",
            },
            youtube: {
                id: "UCFBMYqa053cUwX1tiXDBQ2g",
                username: "@ChiranjeeviHanumanofficial",
            },
        },
    },
    {
        id: "sundar-co",
        name: "Sundar & Co.",
        platforms: {
            instagram: {
                id: "24209732041979060",
                username: "sundarcoofficial",
            },
            youtube: {
                id: "UCYqRDFje9bUDVkHYdTynv7Q",
                username: "@SundarAndCo",
            },
        },
    },
    {
        id: "mind-your-body",
        name: "Mind Your Body",
        platforms: {
            instagram: {
                id: "32157342063913485",
                username: "mindyourbody.in",
            },
            youtube: {
                id: "UCWjMakR-NNd9StKpK9x7XiA",
                username: "@MindYourBody_in",
            },
        },
    },
    {
        id: "trilok",
        name: "Trilok",
        platforms: {
            instagram: {
                id: "25987083924214929",
                username: "trilokindiaofficial",
            },
            youtube: {
                id: "UCgTre9yRdSKkhVsMHx9cSPQ",
                username: "@trilokindiaofficial",
            },
        },
    },
    {
        id: "marathi-minded",
        name: "Marathi Minded",
        platforms: {
            instagram: {
                id: "32101984806083564",
                username: "marathiminded",
            },
            youtube: {
                id: "UC0F74xYbGZ5YSoHk6D2gAPw",
                username: "@MarathiMinded",
            },
        },
    },
    {
        id: "kavya-mehra",
        name: "Kavya Mehra",
        platforms: {
            instagram: {
                id: "25123636823989895",
                username: "therealkavyamehra",
            },
        },
    },
    {
        id: "kabir-manja",
        name: "Kabir Manja",
        platforms: {
            instagram: { id: "9977373789058129", username: "kabir.manja" },
        },
    },
    {
        id: "radhika",
        name: "Radhika",
        platforms: {
            instagram: {
                id: "24111496818487526",
                username: "indiawithradhika",
            },
        },
    },
].toSorted((a, b) => a.name.localeCompare(b.name));

const Index = () => {
    const today = new Date().toISOString().split("T")[0];
    const [dateError, setDateError] = useState("");
    const [downloadError, setDownloadError] = useState(null);
    const [downloadWarning, setDownloadWarning] = useState(null);
    const [tempSelectedAccounts, setTempSelectedAccounts] = useState([]);

    const {
        params: searchParams,
        setOption: setSearchParam,
        setOptions: setSearchParams,
        loading: paramsLoading,
    } = useSearchParams(
        {
            account: {
                default: null,
                validate: (val) => {
                    // Handle both single string and comma-separated string (for multiple accounts)
                    if (!val) return true; // null is valid (no selection)
                    const accountIds = val.includes(",")
                        ? val.split(",")
                        : [val];
                    const supportedPlatformNames = SupportedPlatforms.map(
                        (p) => p.name
                    );
                    return accountIds.every((id) =>
                        ChannelAccountList.some(
                            (acc) =>
                                acc.id === id &&
                                Object.keys(acc.platforms).some((platform) =>
                                    supportedPlatformNames.includes(platform)
                                )
                        )
                    );
                },
            },
            platform: {
                default: null,
                validate: (val) => {
                    // return ["instagram", "youtube"].includes(val);
                    return (
                        val === null ||
                        SupportedPlatforms.some(
                            (platform) => platform.name === val
                        )
                    );
                },
            },
            start_date: {
                default: new Date(new Date().setDate(new Date().getDate() - 7))
                    .toISOString()
                    .split("T")[0],
                validate: (val) => {
                    // Just check if it's a valid date
                    return !isNaN(new Date(val).getTime());
                },
                onInvalid: "default",
            },
            end_date: {
                default: today,
                validate: (val) => {
                    // Just check if it's a valid date
                    return !isNaN(new Date(val).getTime());
                },
                onInvalid: "default",
            },
        },
        true
    );

    const [timeRange, setTimeRange] = useState({
        start: searchParams.start_date,
        end: searchParams.end_date,
    });

    // keep local timeRange in sync when search params change (e.g., on initial load or external updates)
    useEffect(() => {
        setTimeRange({
            start: searchParams.start_date,
            end: searchParams.end_date,
        });
        setDateError("");
    }, [searchParams.start_date, searchParams.end_date]);

    // validate timeRange and show errors, but don't auto-sync back to avoid loops
    useEffect(() => {
        const { start, end } = timeRange;

        if (!start || !end) {
            setDateError("Both start and end dates are required.");
            return;
        }

        const startDate = new Date(start);
        const endDate = new Date(end);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            setDateError("Invalid date format.");
            return;
        }

        // Normalize to UTC midnight to avoid timezone issues when comparing days
        const startUtc = Date.UTC(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate()
        );
        const endUtc = Date.UTC(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate()
        );

        const dayMs = 24 * 60 * 60 * 1000;
        const diff = endUtc - startUtc;

        if (diff <= 0) {
            setDateError("End date must be after start date.");
            return;
        }

        if (diff < dayMs) {
            setDateError("Date range must be at least 1 day apart.");
            return;
        }

        // valid - clear error
        setDateError("");
    }, [timeRange]);

    const { execute: downloadExecute, loading: downloadLoading } =
        useAuthAccessApi({
            onSuccess: (data, response, fullResponse) => {
                console.log("Download Response:", data);
                console.log("Full API Response:", fullResponse);

                setDownloadError(null);
                setDownloadWarning(null);

                // Handle file download
                if (data?.download_url) {
                    window.open(data.download_url, "_blank");

                    // Check for partial success with failed accounts
                    if (
                        data.partial_success &&
                        data.failed_accounts?.length > 0
                    ) {
                        const failedNames = data.failed_accounts
                            .map((acc) => acc.name)
                            .join(", ");
                        setDownloadWarning(
                            `Download generated successfully. However, ${data.failed_accounts.length} account(s) failed to process: ${failedNames}`
                        );
                    }
                } else {
                    console.warn("No download URL found in response");
                    setDownloadError("No download URL received from server");
                }
            },
            onError: (error) => {
                console.error("Error downloading data file:", error);
                setDownloadWarning(null);
                setDownloadError(error.message || "Failed to download file");
            },
        });

    // Filter accounts to only show those with supported platforms
    const filteredChannelAccountList = useMemo(() => {
        const supportedPlatformNames = SupportedPlatforms.map((p) => p.name);
        return ChannelAccountList.filter((account) => {
            // Check if account has at least one supported platform
            return Object.keys(account.platforms).some((platform) =>
                supportedPlatformNames.includes(platform)
            );
        });
    }, []);

    const selectedAccounts = useMemo(() => {
        if (!searchParams.account) return [];
        const accountIds = searchParams.account.includes(",")
            ? searchParams.account.split(",")
            : [searchParams.account];
        return filteredChannelAccountList.filter((acc) =>
            accountIds.includes(acc.id)
        );
    }, [searchParams.account, filteredChannelAccountList]);

    // Sync temp selection with URL params
    useEffect(() => {
        setTempSelectedAccounts(selectedAccounts.map((acc) => acc.id));
    }, [selectedAccounts]);

    const isMultipleMode = selectedAccounts.length > 1;
    const selectedAccount = isMultipleMode ? null : selectedAccounts[0];

    // Check if all selected accounts support the current platform
    const platformValidation = useMemo(() => {
        if (!searchParams.platform || selectedAccounts.length === 0) {
            return { isValid: true, unsupportedAccounts: [] };
        }

        const unsupportedAccounts = selectedAccounts.filter(
            (account) => !account.platforms[searchParams.platform]
        );

        return {
            isValid: unsupportedAccounts.length === 0,
            unsupportedAccounts,
        };
    }, [selectedAccounts, searchParams.platform]);

    const downloadDataFile = useCallback(async () => {
        try {
            await downloadExecute({
                url: API_ENDPOINTS.CREATORS_ACCOUNTS,
                method: "POST",
                body: {
                    accounts: selectedAccounts.map((account) => ({
                        account_id:
                            account.platforms[searchParams.platform]?.id,
                        username:
                            account.platforms[searchParams.platform]?.username,
                        account_name: account.name,
                    })),
                    platform: searchParams.platform,
                    start_date: searchParams.start_date,
                    end_date: searchParams.end_date,
                },
            });
        } catch (error) {
            // Error already handled in onError callback
        }
    }, [downloadExecute, searchParams, selectedAccounts]);

    const { data: channelData, isLoading: channelDataLoading } = useSWR(() =>
        selectedAccount
            ? `${API_ENDPOINTS.CREATORS_ACCOUNTS}?platform=${searchParams.platform}&account_id=${selectedAccount.platforms[searchParams.platform]?.id}&start_date=${searchParams.start_date}&end_date=${searchParams.end_date}`
            : null
    );

    const handleDateChange = (field, value) => {
        const newRange = { ...timeRange, [field]: value };
        setTimeRange(newRange);
        setDateError("");

        // Validate and sync to URL if valid
        const start = field === "start" ? value : newRange.start;
        const end = field === "end" ? value : newRange.end;

        if (!start || !end) return;

        const startDate = new Date(start);
        const endDate = new Date(end);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;

        const startUtc = Date.UTC(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate()
        );
        const endUtc = Date.UTC(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate()
        );

        const dayMs = 24 * 60 * 60 * 1000;
        const diff = endUtc - startUtc;

        // Only sync to URL if valid (at least 1 day apart and end > start)
        if (diff >= dayMs && endUtc > startUtc) {
            setSearchParams({ start_date: start, end_date: end });
        }
    };

    const removeUnsupportedAccounts = useCallback(() => {
        const supportedAccountIds = selectedAccounts
            .filter((account) => account.platforms[searchParams.platform])
            .map((acc) => acc.id);

        if (supportedAccountIds.length === 0) {
            setSearchParams({ account: null, platform: null });
        } else if (supportedAccountIds.length === 1) {
            setSearchParam("account", supportedAccountIds[0]);
        } else {
            setSearchParam("account", supportedAccountIds.join(","));
        }
    }, [
        selectedAccounts,
        searchParams.platform,
        setSearchParam,
        setSearchParams,
    ]);

    const handleSelectChange = (e) => {
        const value = e.target.value;
        const accountIds = typeof value === "string" ? value.split(",") : value;
        setTempSelectedAccounts(accountIds);
    };

    const handleSelectClose = () => {
        if (tempSelectedAccounts.length === 0) {
            setSearchParams({ account: null, platform: null });
            return;
        }

        const accounts = filteredChannelAccountList.filter((acc) =>
            tempSelectedAccounts.includes(acc.id)
        );

        if (tempSelectedAccounts.length > 1) {
            setSearchParam("account", tempSelectedAccounts.join(","));
        } else {
            // Single account selection
            const account = accounts[0];
            const supportedPlatformNames = SupportedPlatforms.map(
                (p) => p.name
            );
            const firstSupportedPlatform = Object.keys(account.platforms).find(
                (platform) => supportedPlatformNames.includes(platform)
            );

            setSearchParams({
                account: account.id,
                platform: firstSupportedPlatform,
            });
        }
    };

    const loadingState = useMemo(
        () => channelDataLoading || downloadLoading,
        [channelDataLoading, downloadLoading]
    );

    if (paramsLoading) {
        return null;
    }

    return (
        <Container>
            <Grid2
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                marginBottom="2rem"
            >
                <Grid2 item>
                    <Typography variant="h6">Account Insights</Typography>
                </Grid2>
                <Grid2 item>
                    {selectedAccounts.length > 0 && (
                        <Box display="flex" gap="1rem">
                            <Box>
                                <InputLabel
                                    id="start-date-label"
                                    sx={{ fontSize: "0.8rem" }}
                                >
                                    From
                                </InputLabel>
                                <Input
                                    type="date"
                                    id="start-date-label"
                                    value={timeRange.start}
                                    sx={dateInputStyle}
                                    inputProps={{ max: today }}
                                    onChange={(e) =>
                                        handleDateChange(
                                            "start",
                                            e.target.value
                                        )
                                    }
                                    disabled={loadingState}
                                />
                            </Box>
                            <Box>
                                <InputLabel
                                    id="end-date-label"
                                    sx={{ fontSize: "0.8rem" }}
                                >
                                    To
                                </InputLabel>
                                <Input
                                    type="date"
                                    id="end-date-label"
                                    value={timeRange.end}
                                    sx={dateInputStyle}
                                    inputProps={{ max: today }}
                                    onChange={(e) =>
                                        handleDateChange("end", e.target.value)
                                    }
                                    error={!!dateError}
                                    disabled={loadingState}
                                />
                            </Box>
                        </Box>
                    )}
                    {dateError && selectedAccounts.length > 0 && (
                        <Typography
                            color="error"
                            variant="caption"
                            sx={{ display: "block", mt: 0.5 }}
                        >
                            {dateError}
                        </Typography>
                    )}
                </Grid2>
            </Grid2>

            <Box
                width="100%"
                display="flex"
                justifyContent="flex-start"
                gap="1rem"
                mb={3}
            >
                <FormControl fullWidth sx={{ maxWidth: 320 }}>
                    <InputLabel id="select-account-label">
                        Select Account{selectedAccounts.length > 1 ? "s" : ""}
                    </InputLabel>
                    <Select
                        labelId="select-account-label"
                        id="account-select"
                        multiple
                        value={tempSelectedAccounts}
                        label={`Select Account${selectedAccounts.length > 1 ? "s" : ""}`}
                        onChange={handleSelectChange}
                        onClose={handleSelectClose}
                        disabled={loadingState}
                        renderValue={(selected) => {
                            const names = filteredChannelAccountList
                                .filter((acc) => selected.includes(acc.id))
                                .map((acc) => acc.name);
                            return names.join(", ");
                        }}
                    >
                        {filteredChannelAccountList.map((account) => (
                            <MenuItem
                                key={account.id}
                                value={account.id}
                                disableRipple
                            >
                                <Checkbox
                                    checked={tempSelectedAccounts.includes(
                                        account.id
                                    )}
                                />
                                <ListItemText primary={account.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {selectedAccounts.length > 0 && (
                    <>
                        {selectedAccounts.length > 1 && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() =>
                                    setSearchParams({
                                        account: null,
                                    })
                                }
                                disabled={loadingState}
                                sx={{
                                    textTransform: "none",
                                    minWidth: "fit-content",
                                }}
                            >
                                Clear All
                            </Button>
                        )}
                        <Box
                            flexGrow={1}
                            justifyContent="flex-end"
                            display="flex"
                            gap={2}
                        >
                            {(isMultipleMode ||
                                (!channelDataLoading && channelData)) && (
                                <Button
                                    variant="outlined"
                                    endIcon={
                                        downloadLoading ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <DownloadIcon size={20} />
                                        )
                                    }
                                    color="info"
                                    sx={{
                                        textTransform: "none",
                                        padding: "0rem 2rem !important",
                                    }}
                                    disabled={
                                        loadingState ||
                                        !platformValidation.isValid
                                    }
                                    onClick={downloadDataFile}
                                >
                                    Download
                                </Button>
                            )}
                            <ButtonGroup variant="outlined">
                                {SupportedPlatforms.map((platformInfo) => {
                                    // In multi-select mode, show all platforms
                                    // In single-select mode, only show platforms the account has
                                    if (!isMultipleMode) {
                                        const someAccountHasPlatform =
                                            selectedAccounts.some(
                                                (account) =>
                                                    account.platforms[
                                                        platformInfo.name
                                                    ]
                                            );
                                        if (!someAccountHasPlatform)
                                            return null;
                                    }

                                    return (
                                        <Button
                                            key={platformInfo.name}
                                            variant={
                                                searchParams.platform ===
                                                platformInfo.name
                                                    ? "contained"
                                                    : "outlined"
                                            }
                                            sx={{
                                                textTransform: "none",
                                                padding: "0rem 2rem !important",
                                            }}
                                            onClick={() =>
                                                setSearchParam(
                                                    "platform",
                                                    platformInfo.name
                                                )
                                            }
                                            disabled={loadingState}
                                        >
                                            {platformInfo.label}
                                        </Button>
                                    );
                                })}
                            </ButtonGroup>
                        </Box>
                    </>
                )}
            </Box>
            <Box
                width="100%"
                display="flex"
                flexDirection="column"
                alignItems="stretch"
            >
                {downloadError && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setDownloadError(null)}
                    >
                        {downloadError}
                    </Alert>
                )}
                {downloadWarning && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        onClose={() => setDownloadWarning(null)}
                    >
                        {downloadWarning}
                    </Alert>
                )}
                {!platformValidation.isValid && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        action={
                            <Button
                                color="primary"
                                size="small"
                                onClick={removeUnsupportedAccounts}
                                sx={{
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    alignSelf: "stretch",
                                    paddingInline: 2,
                                }}
                            >
                                Remove{" "}
                                {platformValidation.unsupportedAccounts
                                    .length === 1
                                    ? "Account"
                                    : "Accounts"}
                            </Button>
                        }
                    >
                        <strong>
                            {platformValidation.unsupportedAccounts
                                .map((acc) => acc.name)
                                .join(", ")}{" "}
                        </strong>
                        {platformValidation.unsupportedAccounts.length === 1
                            ? "is"
                            : "are"}{" "}
                        not available for{" "}
                        <strong>{searchParams.platform}</strong>. Please switch
                        to a different platform or remove{" "}
                        {platformValidation.unsupportedAccounts.length === 1
                            ? "this account"
                            : "these accounts"}{" "}
                        from your selection.
                    </Alert>
                )}
                {isMultipleMode && (
                    <Box
                        sx={{
                            mt: 5,
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: "text.secondary",
                                textAlign: "center",
                            }}
                        >
                            You are viewing multiple accounts. Please select a
                            single account to view detailed insights.
                        </Typography>
                        <Box mt={5}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                gutterBottom
                            >
                                Selected{" "}
                                {selectedAccounts.length === 1
                                    ? "Account"
                                    : "Accounts"}
                                :
                            </Typography>
                            <Box
                                component="ul"
                                sx={{
                                    margin: 0,
                                    paddingLeft: 3,
                                    listStyleType: "disc",
                                }}
                            >
                                {selectedAccounts.map((acc) => (
                                    <Box
                                        component="li"
                                        key={acc.id}
                                        sx={{ mb: 0.5 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            component="span"
                                        >
                                            {acc.name}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
                {!isMultipleMode && channelDataLoading && (
                    <CircularProgress
                        sx={{
                            mx: "auto",
                            mt: 5,
                        }}
                    />
                )}
                {!isMultipleMode &&
                    !channelDataLoading &&
                    channelData &&
                    selectedAccount && (
                        <ChannelInsightsDisplay
                            channelData={channelData}
                            platform={searchParams.platform}
                            account={selectedAccount}
                        />
                    )}
            </Box>
        </Container>
    );
};

export default Index;
